// #region Dependencias
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { deletePageRequest, getPagesRequest } from 'store/thunks/pagesThunks'
import {
  ButtonLink,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  FlexItem,
  IconButton,
  Table
} from 'components/Library'
import { colors } from 'components/Library/theme'
import { IconDelete, IconEdit, IconOpen } from 'components/Icons'
import Dialog from 'components/Popups/Dialog'
import { setDialogoAbierto } from 'store/slices/uiSlice'
import IconAnalytics from 'components/Icons/IconAnalytics'
// #endregion

/**
 * Vista que renderiza una tabla con la lista de todas las paginas.
 * @class
 * @module Pages
 */
const PageList = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { paginas } = useSelector((state) => state.pages)

  /**
   * Objeto de columnas de la tabla.
   */
  const columns = useMemo(
    () => [
      {
        accessorFn: (row) => row.url,
        id: 'url',
        header: 'Enlace',
        cell: (row) => (
          <FlexItem>
            {`${window.location.host}/${row.getValue()}`}
            <IconButton
              color={colors.primary}
              icon={IconOpen}
              title={`Abrir: ${window.location.host}/${row.getValue()}`}
              onClick={() => window.open(`/${row.getValue()}`)}
              style={{
                margin: 0,
                padding: '.25rem'
              }}
            />
          </FlexItem>
        )
      },
      {
        accessorFn: (row) => row.opciones.ES.titulo,
        id: 'titulo',
        header: 'Título',
        cell: (row) => row.getValue()
      },
      {
        accessorFn: (row) => row.opciones.ES.descripcion,
        id: 'descripcion',
        header: 'Descripción',
        cell: (row) => row.getValue()
      },
      {
        accessorFn: (row) => row.fechaCreacion,
        id: 'fechaCreacion',
        header: 'Fecha de creación',
        cell: (row) => dayjs(row.getValue()).format('DD/MMM/YYYY')
      },
      {
        accessorFn: (row) => row.fechaActualizacion,
        id: 'fechaActualizacion',
        header: 'Fecha de actualización',
        cell: (row) => dayjs(row.getValue()).format('DD/MMM/YYYY')
      },
      {
        accessorFn: (row) => row.url,
        id: 'id',
        header: 'Acciones',
        cell: (info) => (
          <span
            style={{
              whiteSpace: 'nowrap'
            }}
          >
            <IconButton
              hover
              color={colors.tertiary}
              icon={IconAnalytics}
              title="Analíticas"
              onClick={() => navigate(`/analytics/pages/${info.row.original.url}`)}
            />
            <IconButton
              hover
              color={colors.secondary}
              icon={IconEdit}
              title="Editar"
              onClick={() => navigate(`/pages/${info.row.original.url}/editor`)}
            />
            <IconButton
              hover
              color={colors.error}
              icon={IconDelete}
              title="Eliminar"
              onClick={() => handleDeletePage(info.row.original)}
            />
          </span>
        )
      }
    ],
    []
  )

  /**
   * Hook useEffect para obtener todas los paginas usando el thunk {@link getPagesRequest}
   * si no se han cargado inicialmente.
   */
  useEffect(() => {
    dispatch(getPagesRequest())
  }, [dispatch])

  /**
   * Función que maneja la eliminación de una pagina mediante un dialogo
   * con el thunk {@link deletePageRequest}
   */
  const handleDeletePage = (pagina) => {
    dispatch(
      setDialogoAbierto({
        abierto: true,
        tipo: 'confirm',
        titulo: '¿Estas seguro de eliminar esta pagina?',
        mensaje: `Se eliminará la página: "${pagina.opciones.ES.titulo}"`,
        callback: () => dispatch(deletePageRequest(pagina))
      })
    )
  }

  return (
    <>
      <Dialog borderradius={2} color={colors.primary} />
      <Card style={{ margin: '2rem auto', width: '95%' }}>
        <CardHeader color="primary">Lista de paginas</CardHeader>
        <CardBody>
          <Table columns={columns} data={paginas} />
        </CardBody>
        <CardFooter
          style={{
            borderTop: 0,
            paddingTop: 0
          }}
        >
          <ButtonLink
            borderradius={2}
            color="tertiary"
            to="/analytics/global"
            text="Estadísticas"
            style={{
              display: 'block',
              width: 'fit-content'
            }}
          />
          <ButtonLink
            borderradius={2}
            color="primary"
            to="/pages/creator"
            text="Nueva página"
            style={{
              display: 'block',
              width: 'fit-content'
            }}
          />
        </CardFooter>
      </Card>
    </>
  )
}

export default PageList
